import { Action, createFeatureSelector, createReducer, createSelector, on } from '@ngrx/store';
import { EPR_ROBOTIC_AEROMEXICO, OptionType } from 'src/app/core/enums/enums';
import { Manual } from 'src/app/core/models/Manual';
import { BreadcrumbRoute, ContractError, IAlertMessage } from 'src/app/core/models/models';
import * as LayoutActions from '../actions/layout.actions';
import { IBookingCredentialsState } from 'src/app/core/models/booking-credentials.model';
import { environment } from 'src/environments/environment';

export interface LayoutState {
  breadcrumb: BreadcrumbRoute[];
  alertMessages: IAlertMessage[];
  bookingCredentials: IBookingCredentialsState;
  error: ContractError;
  openChangePassword: boolean;
  manualInfo: Manual;
  manual: string;
  language: string;
  receivedFrom: string;
}

export const initialLayoutState: LayoutState = {
  breadcrumb: [],
  alertMessages: [],
  bookingCredentials: {
    data: environment.production
      ? {
          username: '',
          password: '',
          sine: '',
          dutyCode: '',
          station: '',
          office: '',
          pcc: '',
          ipcc: ''
        }
      : EPR_ROBOTIC_AEROMEXICO,
    isRequired: false,
    initialRequirement: true,
    loginStatus: {
      isLoading: false,
      isLoggedIn: false,
      loginResponse: '',
      isError: false
    }
  },
  error: null,
  openChangePassword: false,
  manualInfo: null,
  manual: null,
  language: null,
  receivedFrom: null
};

const layoutReducer = createReducer(
  initialLayoutState,
  on(LayoutActions.ChangeBreadcrumb, (state, { breadcrumb }) => ({
    ...state,
    breadcrumb
  })),
  on(LayoutActions.SaveNewAlertMessage, (state, { alertMessage }) => {
    const alertMessagesState = state.alertMessages;
    const index = alertMessagesState.findIndex(
      (message) => message.message === alertMessage.message
    );

    if (index > -1) {
      alertMessagesState.splice(index, 1);
    }
    return {
      ...state,
      alertMessages: [...state.alertMessages, alertMessage]
    };
  }),
  on(LayoutActions.RemoveAlertMessage, (state, { alertMessage }) => {
    const alertMessagesState = state.alertMessages;
    const index = alertMessagesState.indexOf(alertMessage);
    if (index > -1) {
      alertMessagesState.splice(index, 1);
    }
    return {
      ...state,
      alertMessages: alertMessagesState
    };
  }),
  on(LayoutActions.GenericError, (state, { error }) => ({
    ...state,
    error
  })),
  on(LayoutActions.CloseError, (state) => ({
    ...state,
    error: null
  })),
  on(LayoutActions.RequireBookingCredentials, (state) => ({
    ...state,
    bookingCredentials: {
      ...state.bookingCredentials,
      isRequired: true,
      initialRequirement: false,
      loginStatus: {
        isLoading: false,
        isLoggedIn: false,
        loginResponse: '',
        isError: false
      }
    }
  })),
  on(LayoutActions.UpdateBookingCredentials, (state, { credentials }) => ({
    ...state,
    bookingCredentials: {
      data: credentials,
      isRequired: false,
      initialRequirement: false,
      loginStatus: state.bookingCredentials.loginStatus
    }
  })),
  on(LayoutActions.OpenChangePassword, (state, { openChangePassword }) => ({
    ...state,
    openChangePassword
  })),
  on(LayoutActions.ChangePassword, (state) => ({
    ...state,
    loading: true
  })),
  on(LayoutActions.ChangePasswordSuccess, (state) => ({
    ...state,
    loading: false,
    openChangePassword: false
  })),
  on(LayoutActions.LoadManualInfoSuccess, (state, { manualInfo }) => ({
    ...state,
    manualInfo
  })),
  on(LayoutActions.DownloadManualSuccess, (state, { manual }) => ({
    ...state,
    manual
  })),
  on(LayoutActions.SaveLanguage, (state, { language }) => ({
    ...state,
    language
  })),
  on(LayoutActions.SetReceivedFrom, (state, { receivedFrom }) => ({
    ...state,
    receivedFrom
  })),
  on(LayoutActions.RemoveReceivedFrom, (state) => ({
    ...state,
    receivedFrom: null
  })),
  on(LayoutActions.SabreAirlineEPRLogin, (state) => ({
    ...state,
    bookingCredentials: {
      ...state.bookingCredentials,
      loginStatus: {
        isLoading: true,
        isLoggedIn: false,
        loginResponse: '',
        isError: false
      }
    }
  })),
  on(LayoutActions.SabreAirlineEPRLoginSuccess, (state, { info }) => ({
    ...state,
    bookingCredentials: {
      ...state.bookingCredentials,
      loginStatus: {
        isLoading: false,
        isLoggedIn: true,
        loginResponse: info,
        isError: false
      }
    }
  })),
  on(LayoutActions.SabreAirlineEPRLoginError, (state, { message }) => ({
    ...state,
    bookingCredentials: {
      ...state.bookingCredentials,
      loginStatus: {
        isLoading: false,
        isLoggedIn: false,
        loginResponse: message,
        isError: true
      }
    }
  }))
);

export function reducer(state: LayoutState | undefined, action: Action): LayoutState {
  return layoutReducer(state, action);
}

export const selectLayaoutState = createFeatureSelector<LayoutState>(OptionType.Layout);

export const selectBredcrumb = createSelector(selectLayaoutState, (state) => state.breadcrumb);

export const selectAlertMessages = createSelector(
  selectLayaoutState,
  (state) => state.alertMessages
);

export const selectError = createSelector(selectLayaoutState, (state) => state.error);

export const selectOpenChangePassword = createSelector(
  selectLayaoutState,
  (state) => state.openChangePassword
);

export const selectManualInfo = createSelector(selectLayaoutState, (state) => state.manualInfo);

export const selectManualContent = createSelector(selectLayaoutState, (state) => state.manual);

export const selectLanguage = createSelector(selectLayaoutState, (state) => state.language);

export const selectBookingCredentialsState = createSelector(
  selectLayaoutState,
  (state) => state.bookingCredentials
);

export const selectReceivedFrom = createSelector(selectLayaoutState, (state) => state.receivedFrom);
